import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { 
  Plus, 
  Edit, 
  Trash2, 
  X, 
  Briefcase,
  Play,
  AlertTriangle,
  Loader2,
  Brain,
  CheckCircle,
  Eye,
  User,
  Calendar,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { jobDescriptionAPI, aiModuleAPI } from "@/services/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import AIModule from "./AIModule.jsx";
import ModernPageTemplate, { 
  ModernButton, 
  ModernBadge, 
  ModernLoading,
  ModernPaginationFooter
} from "@/components/common/ModernPageTemplate";

const JobDescription = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const aiModuleRef = useRef(null);
  
  // Constants
  const JOB_DESCRIPTIONS_PER_PAGE = 5;
  
  // State management
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [skillInput, setSkillInput] = useState("");
  const [isAIModuleModalOpen, setIsAIModuleModalOpen] = useState(false);
  const [selectedJobForAI, setSelectedJobForAI] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(JOB_DESCRIPTIONS_PER_PAGE);
  const [isPaginationEnabled, setIsPaginationEnabled] = useState(true);
  
  // State for delete confirmation
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);

  // Sorting states
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Form state
  const [formData, setFormData] = useState({
    jobTitle: "",
    description: "",
    skills: [],
    experienceLevel: "",
    experienceRange: {
      minYears: "",
      maxYears: ""
    },
    passPercentage: 60
  });

  // Effect to adjust footer position based on sidebar state
  useEffect(() => {
    const adjustFooterPosition = () => {
      const footer = document.querySelector('.admin-fixed-footer');
      if (!footer) return;

      // Check if we're on desktop (lg breakpoint)
      if (window.innerWidth >= 1024) {
        // Look for the main content container to determine sidebar state
        const mainContent = document.querySelector('[class*="lg:pl-"]');
        if (mainContent) {
          const classes = mainContent.className;
          if (classes.includes('lg:pl-14')) {
            // Sidebar is collapsed (68px width)
            footer.style.left = '68px';
          } else if (classes.includes('lg:pl-64')) {
            // Sidebar is expanded (256px width)
            footer.style.left = '256px';
          }
        }
      } else {
        // Mobile/tablet - full width
        footer.style.left = '0px';
      }
    };

    // Initial adjustment
    adjustFooterPosition();

    // Listen for window resize
    window.addEventListener('resize', adjustFooterPosition);

    // Use MutationObserver to watch for class changes on the main content
    const observer = new MutationObserver(adjustFooterPosition);
    const mainContent = document.querySelector('[class*="lg:pl-"]');
    if (mainContent) {
      observer.observe(mainContent, { 
        attributes: true, 
        attributeFilter: ['class'] 
      });
    }

    return () => {
      window.removeEventListener('resize', adjustFooterPosition);
      observer.disconnect();
    };
  }, []);

  // Fetch job descriptions
  const { data: jobDescriptions, isLoading, error } = useQuery({
    queryKey: ['job-descriptions', searchTerm, currentPage, isPaginationEnabled, rowsPerPage],
    queryFn: async () => {
      try {
        const response = await jobDescriptionAPI.getAll({
          page: isPaginationEnabled ? currentPage : 1,
          limit: isPaginationEnabled ? rowsPerPage : 1000, // Large number to get all records
          search: searchTerm
        });
        return response;
      } catch (error) {
        console.error('❌ Job descriptions fetch error:', error);
        console.error('Error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message
        });
        throw error;
      }
    },
    retry: 1,
    onError: (error) => {
      console.error('Query error:', error);
      toast({
        title: "Error Loading Job Descriptions",
        description: error.response?.data?.message || error.message || "Failed to load job descriptions",
        variant: "destructive",
      });
    }
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: jobDescriptionAPI.create,
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Job description created successfully",
      });
      setIsCreateModalOpen(false);
      resetForm();
      setCurrentPage(1); // Reset to first page to see the new item
      queryClient.invalidateQueries(['job-descriptions']);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to create job description",
        variant: "destructive",
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => jobDescriptionAPI.update(id, data),
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Job description updated successfully",
      });
      setIsEditModalOpen(false);
      setEditingJob(null);
      resetForm();
      queryClient.invalidateQueries(['job-descriptions']);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update job description",
        variant: "destructive",
      });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: jobDescriptionAPI.delete,
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Job description deleted successfully",
      });
      
      // Check if we need to go back a page after deletion
      const remainingItems = (jobDescriptions?.data?.data?.pagination?.totalCount || 0) - 1;
      const maxPageAfterDeletion = Math.ceil(remainingItems / rowsPerPage);
      
      if (currentPage > maxPageAfterDeletion && maxPageAfterDeletion > 0) {
        setCurrentPage(maxPageAfterDeletion);
      }
      
      queryClient.invalidateQueries(['job-descriptions']);
      setIsDeleteConfirmOpen(false);
      setJobToDelete(null);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete job description",
        variant: "destructive",
      });
    },
  });

  // Form handlers
  const resetForm = () => {
    setFormData({
      jobTitle: "",
      description: "",
      skills: [],
      experienceLevel: "",
      experienceRange: {
        minYears: "",
        maxYears: ""
      },
      passPercentage: 60
    });
    setSkillInput("");
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleExperienceRangeChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      experienceRange: {
        ...prev.experienceRange,
        [field]: value
      }
    }));
  };

  const addSkill = () => {
    if (skillInput.trim() && !formData.skills.includes(skillInput.trim())) {
      const newSkills = [...formData.skills, skillInput.trim()];
      
      // Update skills
      setFormData(prev => ({
        ...prev,
        skills: newSkills
      }));
      
      // Auto-adjust pass percentage if current percentage is not valid for new skill count
      const currentPercentage = formData.passPercentage;
      const newSkillCount = newSkills.length;
      const validPercentages = [];
      
      for (let i = 1; i <= newSkillCount; i++) {
        validPercentages.push(Math.round((i / newSkillCount) * 100));
      }
      
      // If current percentage is not in valid options, set to closest valid option
      if (!validPercentages.includes(currentPercentage)) {
        const closestPercentage = validPercentages.reduce((prev, curr) => 
          Math.abs(curr - currentPercentage) < Math.abs(prev - currentPercentage) ? curr : prev
        );
        
        setFormData(prevData => ({
          ...prevData,
          passPercentage: closestPercentage
        }));
      }
      
      setSkillInput("");
    }
  };

  const removeSkill = (skillToRemove) => {
    const newSkills = formData.skills.filter(skill => skill !== skillToRemove);
    
    // Update skills
    setFormData(prev => ({
      ...prev,
      skills: newSkills
    }));
    
    // Auto-adjust pass percentage if current percentage is not valid for new skill count
    if (newSkills.length > 0) {
      const currentPercentage = formData.passPercentage;
      const newSkillCount = newSkills.length;
      const validPercentages = [];
      
      for (let i = 1; i <= newSkillCount; i++) {
        validPercentages.push(Math.round((i / newSkillCount) * 100));
      }
      
      // If current percentage is not in valid options, set to closest valid option
      if (!validPercentages.includes(currentPercentage)) {
        const closestPercentage = validPercentages.reduce((prev, curr) => 
          Math.abs(curr - currentPercentage) < Math.abs(prev - currentPercentage) ? curr : prev
        );
        
        setFormData(prevData => ({
          ...prevData,
          passPercentage: closestPercentage
        }));
      }
    } else {
      // If no skills left, reset to default
      setFormData(prevData => ({
        ...prevData,
        passPercentage: 60
      }));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSkill();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.jobTitle.trim()) {
      toast({
        title: "Validation Error",
        description: "Job title is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.description.trim()) {
      toast({
        title: "Validation Error",
        description: "Job description is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.experienceLevel) {
      toast({
        title: "Validation Error",
        description: "Experience level is required",
        variant: "destructive",
      });
      return;
    }

    // Validate experience range for experienced positions
    if (formData.experienceLevel === 'experienced') {
      const minYears = parseInt(formData.experienceRange.minYears);
      const maxYears = parseInt(formData.experienceRange.maxYears);
      
      // Check if both values are provided
      if (formData.experienceRange.minYears !== "" && formData.experienceRange.maxYears !== "") {
        // Ensure maxYears is greater than minYears (not equal)
        if (maxYears <= minYears) {
          toast({
            title: "Validation Error",
            description: "Maximum years must be greater than minimum years",
            variant: "destructive",
          });
          return;
        }
      }
    }

    const submitData = {
      ...formData,
      experienceRange: formData.experienceLevel === 'experienced' ? {
        minYears: formData.experienceRange.minYears !== "" ? parseInt(formData.experienceRange.minYears) : null,
        maxYears: formData.experienceRange.maxYears !== "" ? parseInt(formData.experienceRange.maxYears) : null,
      } : { minYears: null, maxYears: null }
    };

    if (editingJob) {
      updateMutation.mutate({ id: editingJob._id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({
      jobTitle: job.jobTitle,
      description: job.description,
      skills: job.skills || [],
      experienceLevel: job.experienceLevel,
      experienceRange: {
        minYears: job.experienceRange?.minYears || "",
        maxYears: job.experienceRange?.maxYears || ""
      },
      passPercentage: job.passPercentage
    });
    setIsEditModalOpen(true);
  };

  const handleDelete = (job) => {
    setJobToDelete(job);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (jobToDelete) {
      deleteMutation.mutate(jobToDelete._id);
    }
  };

  const handleProceed = (job) => {
    setSelectedJobForAI(job);
    setIsAIModuleModalOpen(true);
  };

  const openCreateModal = () => {
    resetForm();
    setIsCreateModalOpen(true);
  };

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
    // Clear sorting when search changes to show newest first
    setSortField('');
    setSortOrder('asc');
  }, [searchTerm]);

  // Refresh function that properly invalidates queries and resets state
  const handleRefresh = async () => {
    // Reset all state to initial values
    setCurrentPage(1);
    setSortField('');
    setSortOrder('asc');
    setSearchTerm('');
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setEditingJob(null);
    setIsAIModuleModalOpen(false);
    setSelectedJobForAI(null);
    setIsDeleteConfirmOpen(false);
    setJobToDelete(null);
    
    // Invalidate and refetch all related queries
    await queryClient.invalidateQueries({ queryKey: ["job-descriptions"] });
    
    // Show success message
    toast({
      title: "Refreshed",
      description: "Data has been refreshed successfully",
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1" />;
    return sortOrder === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" />
    );
  };

  const sortJobDescriptions = (jobs, sortField, sortOrder) => {
    // If no sorting is applied, return jobs in their original order (newest first from API)
    if (!sortField) return jobs;

    return [...jobs].sort((a, b) => {
      let aValue, bValue;

      // Handle nested properties
      if (sortField.includes('.')) {
        const fields = sortField.split('.');
        aValue = fields.reduce((obj, field) => obj?.[field], a);
        bValue = fields.reduce((obj, field) => obj?.[field], b);
      } else {
        aValue = a[sortField];
        bValue = b[sortField];
      }

      // Handle date fields
      if (sortField === 'createdAt') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }

      // Handle string fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortOrder === 'asc' ? 1 : -1;
      if (bValue == null) return sortOrder === 'asc' ? -1 : 1;

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(parseInt(value));
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const handlePaginationToggle = (checked) => {
    setIsPaginationEnabled(checked);
    setCurrentPage(1); // Reset to first page when toggling pagination
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Generate dynamic skills matching percentage options based on selected skills
  const generateSkillsMatchingOptions = () => {
    const skillsCount = formData.skills.length;
    
    if (skillsCount === 0) {
      // If no skills selected, return empty array to disable dropdown
      return [];
    }
    
    const options = [];
    
    // Generate percentage options based on number of skills that can be matched
    for (let i = 1; i <= skillsCount; i++) {
      const percentage = Math.round((i / skillsCount) * 100);
      options.push({
        value: percentage,
        skillsRequired: i,
        label: `${percentage}%`
      });
    }
    
    // Remove duplicates based on percentage value and sort
    const uniqueOptions = options.filter((option, index, self) => 
      index === self.findIndex(o => o.value === option.value)
    ).sort((a, b) => a.value - b.value);
    
    return uniqueOptions;
  };

  // Get the current skills matching option details
  const getCurrentSkillsMatchingDetails = () => {
    const skillsCount = formData.skills.length;
    if (skillsCount === 0) return null;
    
    const requiredSkills = Math.round((formData.passPercentage / 100) * skillsCount);
    return {
      totalSkills: skillsCount,
      requiredSkills: requiredSkills,
      percentage: formData.passPercentage
    };
  };

  const getStatusBadge = (experienceLevel, experienceRange) => {
    if (experienceLevel === 'fresher') {
      return (
        <ModernBadge variant="template">
          Fresher
        </ModernBadge>
      );
    } else {
      return (
        <ModernBadge variant="language">
          {experienceRange?.minYears || 0}-{experienceRange?.maxYears || 0} years
        </ModernBadge>
      );
    }
  };

  const renderJobDescriptionsTable = () => {
    const jobs = jobDescriptions?.data?.data?.jobDescriptions || [];
    const pagination = jobDescriptions?.data?.data?.pagination || {};

    // Sort the job descriptions
    const sortedJobs = sortJobDescriptions(jobs, sortField, sortOrder);

    return (
      <>
        {isLoading ? (
          <ModernLoading text="Loading job descriptions..." />
        ) : error ? (
          <div className="text-center p-6 text-red-500">
            Error loading job descriptions. Please try again.
          </div>
        ) : sortedJobs.length > 0 ? (
          <>
            {/* Job Descriptions Table */}
            <div className="admin-table-container">
              <table className="admin-table">
                <thead className="admin-table-header">
                  <tr>
                    <th className="admin-table-cell">S.No</th>
                    <th
                      className="admin-table-cell sortable"
                      onClick={() => handleSort('jobTitle')}
                    >
                      <div className="flex items-center cursor-pointer">
                        Job Title
                        {getSortIcon('jobTitle')}
                      </div>
                    </th>
                    <th className="admin-table-cell">Skills</th>
                    <th
                      className="admin-table-cell sortable"
                      onClick={() => handleSort('experienceLevel')}
                    >
                      <div className="flex items-center cursor-pointer">
                        Experience
                        {getSortIcon('experienceLevel')}
                      </div>
                    </th>
                    <th
                      className="admin-table-cell sortable"
                      onClick={() => handleSort('passPercentage')}
                    >
                      <div className="flex items-center cursor-pointer">
                        Pass %
                        {getSortIcon('passPercentage')}
                      </div>
                    </th>
                    <th
                      className="admin-table-cell sortable"
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className="flex items-center cursor-pointer">
                        Created
                        {getSortIcon('createdAt')}
                      </div>
                    </th>
                    <th className="admin-table-cell text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedJobs.map((job, index) => (
                    <tr key={job._id} className="admin-table-row">
                      <td className="admin-table-cell number">
                        {(currentPage - 1) * JOB_DESCRIPTIONS_PER_PAGE + (index + 1)}
                      </td>
                      <td className="admin-table-cell font-medium">
                        <div className="flex items-center">
                          <Briefcase className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium" title={job.jobTitle}>
                              {job.jobTitle}
                            </div>
                            <div className="text-xs text-gray-500" title={job.description}>
                              {job.description}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="admin-table-cell">
                        <div className="flex flex-wrap gap-1">
                          {job.skills?.slice(0, 2).map((skill, index) => (
                            <ModernBadge key={index} variant="language" className="text-xs">
                              {skill}
                            </ModernBadge>
                          ))}
                          {job.skills?.length > 2 && (
                            <ModernBadge variant="template" className="text-xs">
                              +{job.skills.length - 2}
                            </ModernBadge>
                          )}
                        </div>
                      </td>
                      <td className="admin-table-cell">
                        {getStatusBadge(job.experienceLevel, job.experienceRange)}
                      </td>
                      <td className="admin-table-cell">
                        <span className="text-sm font-medium">{job.passPercentage}%</span>
                      </td>
                      <td className="admin-table-cell">
                        <span className="text-xs">
                          {format(new Date(job.createdAt), "MMM dd, yy")}
                        </span>
                      </td>
                      <td className="admin-table-cell text-right">
                        <div className="admin-table-actions">
                          <button
                            className="admin-table-action-btn create"
                            onClick={() => handleProceed(job)}
                            title="Create New Exam"
                          >
                            <Play className="h-3 w-3" />
                          </button>
                          <button
                            className="admin-table-action-btn edit"
                            onClick={() => handleEdit(job)}
                            title="Edit Job Description"
                          >
                            <Edit className="h-3 w-3" />
                          </button>
                          <button
                            className="admin-table-action-btn delete"
                            onClick={() => handleDelete(job)}
                            title="Delete Job Description"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center p-6 text-gray-500">
            No job descriptions found
          </div>
        )}
      </>
    );
  };

  const skillsMatchingOptions = generateSkillsMatchingOptions();
  const currentSkillsDetails = getCurrentSkillsMatchingDetails();

  return (
    <ModernPageTemplate
      title="Job Descriptions"
      contentClassName="job-description-page"
      stats={{
        total: jobDescriptions?.data?.data?.pagination?.totalCount || 0
      }}
      onRefresh={handleRefresh}
      search={{
        placeholder: "Search job descriptions...",
        value: searchTerm,
        onChange: handleSearchChange,
        onSubmit: (e) => e.preventDefault()
      }}
      createAction={{
        onClick: openCreateModal,
        tooltip: "Create Description"
      }}
    >
      {renderJobDescriptionsTable()}

      {/* Reusable Pagination Footer */}
      <ModernPaginationFooter
        isPaginationEnabled={isPaginationEnabled}
        onPaginationToggle={handlePaginationToggle}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        pagination={{
          current: currentPage,
          pages: jobDescriptions?.data?.data?.pagination?.totalPages || 1,
          total: jobDescriptions?.data?.data?.pagination?.totalCount || 0
        }}
        onPageChange={handlePageChange}
        rowsOptions={[5, 10, 20, 50]}
      />

      {/* Create/Edit Modal */}
      <Dialog open={isCreateModalOpen || isEditModalOpen} onOpenChange={(open) => {
        if (!open) {
          setIsCreateModalOpen(false);
          setIsEditModalOpen(false);
          setEditingJob(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingJob ? 'Edit Job Description' : 'Create Job Description'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="jobTitle">Job Title *</Label>
                <Input
                  id="jobTitle"
                  value={formData.jobTitle}
                  onChange={(e) => handleInputChange('jobTitle', e.target.value)}
                  placeholder="e.g., Senior Frontend Developer"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Job Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe the role, responsibilities, and requirements..."
                  rows={4}
                  required
                />
              </div>

              <div>
                <Label htmlFor="skills">Required Skills</Label>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      id="skills"
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Add a skill and press Enter"
                    />
                    <Button type="button" onClick={addSkill} variant="outline">
                      Add
                    </Button>
                  </div>
                  {formData.skills.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.skills.map((skill, index) => (
                        <Badge key={index} variant="secondary" className="flex items-center gap-1">
                          {skill}
                          <X 
                            className="h-3 w-3 cursor-pointer" 
                            onClick={() => removeSkill(skill)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="experienceLevel">Experience Level *</Label>
                <Select 
                  value={formData.experienceLevel} 
                  onValueChange={(value) => handleInputChange('experienceLevel', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select experience level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fresher">Fresher (0 years)</SelectItem>
                    <SelectItem value="experienced">Experienced</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.experienceLevel === 'experienced' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="minYears">Minimum Years</Label>
                    <Input
                      id="minYears"
                      type="number"
                      min="0"
                      max="50"
                      value={formData.experienceRange.minYears}
                      onChange={(e) => handleExperienceRangeChange('minYears', e.target.value)}
                      placeholder="e.g., 0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="maxYears">Maximum Years</Label>
                    <Input
                      id="maxYears"
                      type="number"
                      min="0"
                      max="50"
                      value={formData.experienceRange.maxYears}
                      onChange={(e) => handleExperienceRangeChange('maxYears', e.target.value)}
                      placeholder="e.g., 5"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="passPercentage">
                  Skills Matching Percentage *
                  {formData.skills.length > 0 && (
                    <span className="text-sm text-gray-500 ml-1">
                      (Based on {formData.skills.length} selected skill{formData.skills.length > 1 ? 's' : ''})
                    </span>
                  )}
                </Label>
                <Select 
                  value={formData.passPercentage.toString()} 
                  onValueChange={(value) => handleInputChange('passPercentage', parseInt(value))}
                  disabled={formData.skills.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue 
                      placeholder={
                        formData.skills.length === 0 
                          ? "Add skills first to set matching percentage" 
                          : "Select skills matching percentage"
                      } 
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.skills.length === 0 ? (
                      <SelectItem value="0" disabled>
                        Please add skills first
                      </SelectItem>
                    ) : (
                      skillsMatchingOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value.toString()}>
                          {option.label}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setIsEditModalOpen(false);
                  setEditingJob(null);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {editingJob ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  editingJob ? 'Update Job Description' : 'Create Job Description'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Job Description</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{jobToDelete?.jobTitle}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Module Modal */}
      <Dialog open={isAIModuleModalOpen} onOpenChange={async (open) => {
        if (!open) {
          // Check if AIModule has unsaved changes before closing
          if (aiModuleRef.current && aiModuleRef.current.hasUnsavedChanges) {
            // Call AIModule's handleModalClose method
            aiModuleRef.current.handleModalClose();
            return; // Don't close yet, let AIModule handle it
          }
          
          // No unsaved changes, close normally
          setIsAIModuleModalOpen(false);
          setSelectedJobForAI(null);
        }
      }}>
        <DialogContent className="max-w-[95vw] max-h-[98vh] w-[95vw] h-[98vh] p-0 flex flex-col ai-module-dialog-content overflow-hidden">
          <div className="flex-1 min-h-0 flex flex-col h-full overflow-hidden">
            {selectedJobForAI && (
              <AIModule 
                ref={aiModuleRef}
                jobDescriptionData={selectedJobForAI}
                onClose={() => {
                  setIsAIModuleModalOpen(false);
                  setSelectedJobForAI(null);
                }}
                isModal={true}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </ModernPageTemplate>
  );
};

export default JobDescription;